import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { useFormikContext } from "formik";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import Input from "../../../../../Components/Common/Input/Input";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import { autoFillClass, prefillProps } from "../../utils/prefilled";
import { PreconFormValues } from "../../types/precon.types";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";

interface Props {
  prefilledFields: Set<string>;
}

const Step5Allowances = ({ prefilledFields }: Props) => {
  const { values, errors, touched, handleChange, handleBlur } =
    useFormikContext<PreconFormValues>();

  const af = (field: keyof PreconFormValues) =>
    autoFillClass(field, prefilledFields);
  const pf = (field: keyof PreconFormValues) =>
    prefillProps(field, prefilledFields);
  const err = (field: keyof PreconFormValues) =>
    errors[field] as string | undefined;
  const isTouched = (field: keyof PreconFormValues) =>
    touched[field] as boolean | undefined;

  const hasErr = (
    field: keyof PreconFormValues,
    have?: number,
    min?: number,
  ): boolean => {
    const val = String(values[field] ?? "");
    const len = val.length;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && len > 0 && len < min) return true;
    return !!(isTouched(field) && err(field));
  };

  return (
    <div className="screen active">
      <SectionHeader
        title="Allowances & Contingencies"
        subtitle="Document allowances, contingency amounts, and CSC recommendations relative to industry norms."
      />

      <Card title="Allowances" badge="Editable" badgeType="edit">
        <div className="field">
          <label>
            Allowances Narrative <span className="req">*</span>
          </label>
          <TextArea
            name="allowancesNarrative"
            className={`ft${hasErr("allowancesNarrative", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.allowancesNarrative}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.allowancesNarrative}
            touched={isTouched("allowancesNarrative")}
            error={err("allowancesNarrative")}
          />
        </div>
      </Card>

      <Card title="Contingencies" badge="Editable" badgeType="edit">
        <div className="fieldRow c3">
          <div className="field">
            <label>GC Contingency Amount</label>
            <Input
              name="gcContingencyAmount"
              className={`${af("gcContingencyAmount")}${hasErr("gcContingencyAmount", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.gcContingencyAmount}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("gcContingencyAmount")}
            />
            <ErrorMessage
              value={values.gcContingencyAmount}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("gcContingencyAmount")}
              error={err("gcContingencyAmount")}
            />
          </div>
          <div className="field">
            <label>% of Hard Costs</label>
            <Input
              name="contingencyPct"
              className={`${af("contingencyPct")}${hasErr("contingencyPct", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.contingencyPct}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("contingencyPct")}
            />
            <ErrorMessage
              value={values.contingencyPct}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("contingencyPct")}
              error={err("contingencyPct")}
            />
          </div>
          <div className="field">
            <label>Industry Typical Range</label>
            <Input
              name="industryRange"
              className={`fi${hasErr("industryRange", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.industryRange}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
            />
            <ErrorMessage
              value={values.industryRange}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("industryRange")}
              error={err("industryRange")}
            />
          </div>
        </div>

        <div className="field fieldInline">
          <label>
            Contingency Narrative <span className="req">*</span>
          </label>
          <TextArea
            name="contingencyNarrative"
            className={`ft textareaS${hasErr("contingencyNarrative", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.contingencyNarrative}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.contingencyNarrative}
            touched={isTouched("contingencyNarrative")}
            error={err("contingencyNarrative")}
          />
        </div>

        {values.gcContingencyAmount && values.contingencyPct && (
          <div className="insightBox">
            <strong>CSC Recommendation</strong>
            {` At ${values.contingencyPct}, the stated contingency is below the ${values.industryRange} industry standard. CSC recommends the Lender carry a larger-than-typical contingency to cover potential cost overruns.`}
          </div>
        )}
      </Card>

      <Card
        title="Clarifications, Assumptions & Exclusions"
        badge="Editable"
        badgeType="edit"
      >
        <div className="field">
          <label>Narrative</label>
          <TextArea
            name="clarificationsNarrative"
            className={`ft${hasErr("clarificationsNarrative", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.clarificationsNarrative}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.clarificationsNarrative}
            touched={isTouched("clarificationsNarrative")}
            error={err("clarificationsNarrative")}
          />
        </div>
      </Card>
    </div>
  );
};

export default Step5Allowances;
